import React from "react";
import PropTypes from "prop-types";
import { Chip } from "@mui/material";
import { colors, borderRadiusPx, fontFamilies } from "../../../../theme";

/**
 * CommonChip Component
 * Centralized, reusable status, filter, and tag chip component adhering to the AutoBET theme palette and tokens.
 *
 * Supported status presets / variants:
 * - "filter" / "filterTab": Interactive filter tab chip with active/selected state support
 * - "active" / "success": Green background & text
 * - "pending" / "warning" / "rollover": Amber background & text
 * - "ended" / "closed" / "neutral": Slate/grey background & text
 * - "danger" / "error": Red background & text
 * - "primary" / "info": Brand blue background & text
 * - "sidebarBadge": Red badge background with white text
 */
const CommonChip = ({
  label,
  status,
  variant,
  size = "small",
  color,
  backgroundColor,
  selected,
  active,
  icon,
  onDelete,
  onClick,
  pill = false,
  sx = {},
  ...props
}) => {
  const isFilter =
    variant === "filter" ||
    variant === "filterTab" ||
    variant === "filterActive" ||
    variant === "filterInactive" ||
    selected !== undefined ||
    active !== undefined;

  const isSelected = Boolean(selected || active || variant === "filterActive");

  const st = colors.dashboard?.status || {};
  const chipText = label ?? status ?? "";
  const normalizedKey = (variant || status || chipText || "").toString().toLowerCase().trim();

  let defaultBg = colors.slate[100];
  let defaultTextColor = colors.slate[700];
  let hoverBg = colors.slate[200];

  if (isFilter) {
    if (isSelected) {
      defaultBg = colors.primary.main;
      defaultTextColor = "#ffffff";
      hoverBg = colors.primary.dark;
    } else {
      defaultBg = colors.slate[100];
      defaultTextColor = colors.slate[700];
      hoverBg = colors.slate[200];
    }
  } else if (
    normalizedKey === "active" ||
    normalizedKey === "success" ||
    normalizedKey === "verified" ||
    normalizedKey === "online" ||
    normalizedKey === "online & verified"
  ) {
    defaultBg = st.activeBg || colors.emerald[50];
    defaultTextColor = st.activeText || colors.emerald[700];
  } else if (
    normalizedKey === "pending" ||
    normalizedKey === "warning" ||
    normalizedKey === "in progress" ||
    normalizedKey === "rollover" ||
    normalizedKey === "awaiting" ||
    normalizedKey.includes("awaiting")
  ) {
    defaultBg = st.pendingBg || colors.amber[50];
    defaultTextColor = st.pendingText || colors.amber[700];
  } else if (
    normalizedKey === "ended" ||
    normalizedKey === "closed" ||
    normalizedKey === "neutral" ||
    normalizedKey === "muted" ||
    normalizedKey === "inactive"
  ) {
    defaultBg = st.endedBg || colors.slate[100];
    defaultTextColor = st.endedText || colors.slate[700];
  } else if (
    normalizedKey === "danger" ||
    normalizedKey === "error" ||
    normalizedKey === "failed" ||
    normalizedKey === "rejected"
  ) {
    defaultBg = st.dangerBg || colors.red[50];
    defaultTextColor = st.dangerText || colors.red[700];
  } else if (
    normalizedKey === "primary" ||
    normalizedKey === "info" ||
    normalizedKey === "telemetry" ||
    normalizedKey === "new"
  ) {
    defaultBg = colors.blue[50];
    defaultTextColor = colors.primary.main;
  } else if (normalizedKey === "sidebarbadge") {
    defaultBg = colors.sidebar?.badgeBg || colors.red[500];
    defaultTextColor = colors.sidebar?.badgeText || "#ffffff";
  }

  const finalBg = backgroundColor || (variant === "sidebarBadge" ? colors.sidebar?.badgeBg : defaultBg);
  const finalColor = color || (variant === "sidebarBadge" ? colors.sidebar?.badgeText : defaultTextColor);

  return (
    <Chip
      label={chipText}
      size={size}
      icon={icon}
      onDelete={onDelete}
      onClick={onClick}
      sx={{
        fontFamily: fontFamilies.primary,
        fontWeight: isFilter ? (isSelected ? 700 : 600) : 600,
        fontSize: isFilter ? "0.85rem" : size === "small" ? "0.75rem" : "0.85rem",
        height: isFilter ? "auto" : size === "small" ? 24 : 32,
        py: isFilter ? 0.65 : 0,
        pl: isFilter ? 1.5 : 0,
        pr: isFilter ? 1.25 : 0,
        cursor: onClick || isFilter ? "pointer" : "default",
        borderRadius: pill
          ? borderRadiusPx.pill
          : isFilter
          ? borderRadiusPx.lg
          : borderRadiusPx.sm,
        backgroundColor: finalBg,
        color: isSelected && isFilter ? "#ffffff !important" : finalColor,
        letterSpacing: "0.01em",
        border: "none",
        maxWidth: "100%",
        transition: "all 0.15s ease-in-out",
        "& .MuiChip-label": {
          px: isFilter ? 0 : size === "small" ? 1 : 1.5,
          py: isFilter ? 0.25 : 0,
          color: isSelected && isFilter ? "#ffffff !important" : "inherit",
        },
        "& .MuiChip-icon": {
          color: isSelected && isFilter ? "#ffffff" : finalColor,
          fontSize: "0.85rem",
          ml: isFilter ? 0 : 0.5,
          mr: isFilter ? 0.5 : 0,
        },
        "& .MuiChip-deleteIcon": {
          color: finalColor,
          opacity: 0.7,
          "&:hover": {
            opacity: 1,
            color: finalColor,
          },
        },
        "&:hover":
          onClick || isFilter
            ? {
                backgroundColor: hoverBg,
              }
            : undefined,
        ...sx,
      }}
      {...props}
    />
  );
};

CommonChip.propTypes = {
  label: PropTypes.node,
  status: PropTypes.string,
  variant: PropTypes.oneOf([
    "filter",
    "filterTab",
    "filterActive",
    "filterInactive",
    "active",
    "success",
    "pending",
    "warning",
    "ended",
    "closed",
    "neutral",
    "danger",
    "error",
    "primary",
    "info",
    "sidebarBadge",
  ]),
  selected: PropTypes.bool,
  active: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  icon: PropTypes.element,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  pill: PropTypes.bool,
  sx: PropTypes.object,
};

export default CommonChip;
